import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../api/client";
import { Button, Modal } from "../../components/ui";
import { formatDuration } from "../../lib/format";
import {
  addToOutbox, buildTimedBody, finishTimed, flushOutbox, loadOutbox, loadTimed, pauseTimed, removeFromOutbox, resumeTimed, saveTimed, startTimed,
  timedActiveMs, timedElapsedMs, timedLongEnough, type Timed, type TimedBody, type TimedPost, type TimedSport,
} from "../../lib/timed";
import { navigate } from "../../router/router";
import { t } from "../../i18n";

/**
 * v0.19.0 — cronômetro de Esteira e Fortalecimento: só o tempo, sem GPS. O estado fica no aparelho (sobrevive a
 * recarregar e a tela bloqueada) e o envio é reenviado sozinho se faltar internet. Mesmo fluxo da corrida:
 * Iniciar → Pausar/Retomar → Finalizar → Resumo → Salvar → "Ver atividade".
 */

const newId = () => (typeof crypto !== "undefined" && "randomUUID" in crypto
  ? crypto.randomUUID()
  : "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16)));

const store = (): Storage | null => { try { return window.localStorage; } catch { return null; } };
const NO_STORE = { getItem: () => null, setItem: () => undefined, removeItem: () => undefined };

export function TimedRecorder({ orgId, userId, sport, onActive }: { orgId: string; userId: string; sport: TimedSport; onActive: (active: boolean) => void }) {
  const ls = store() ?? NO_STORE;
  const [tm, setTm] = useState<Timed | null>(() => loadTimed(ls, userId));
  const [now, setNow] = useState(() => Date.now());
  const [askDiscard, setAskDiscard] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [activityId, setActivityId] = useState<string | null>(null);
  const savedClientId = useRef<string | null>(null);

  const post = useCallback<TimedPost>(async (body: TimedBody) => {
    try {
      const d = await api.post<{ activity_id: string }>(`/orgs/${orgId}/me/timed-recordings`, body);
      return { status: 200, activityId: d.activity_id };
    } catch (e) {
      return { status: e instanceof ApiError ? e.status : 0 };
    }
  }, [orgId]);

  const update = useCallback((next: Timed | null) => { setTm(next); saveTimed(ls, userId, next); }, [ls, userId]);
  const pendingCount = loadOutbox(ls, userId, Date.now()).length;

  // reenvio do que ficou na fila: ao abrir, ao voltar a conexão e a cada 30 s
  const flush = useCallback(async () => {
    const done = await flushOutbox(ls, userId, post, Date.now());
    if (savedClientId.current && done[savedClientId.current]) setActivityId(done[savedClientId.current]);
  }, [ls, userId, post]);
  useEffect(() => {
    void flush();
    const online = () => void flush();
    window.addEventListener("online", online);
    const id = window.setInterval(() => void flush(), 30_000);
    return () => { window.removeEventListener("online", online); window.clearInterval(id); };
  }, [flush]);

  // relógio da tela (o tempo vem dos carimbos de horário, não deste intervalo)
  useEffect(() => {
    if (!tm || tm.status === "finalizing") return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [tm?.status]);

  useEffect(() => { onActive(!!tm); }, [tm, onActive]);
  useEffect(() => () => onActive(false), [onActive]);

  const save = async () => {
    if (!tm) return;
    const body = buildTimedBody(tm);
    if (!body) return;
    setBusy(true); setSendError(false);
    addToOutbox(ls, userId, body, Date.now());   // cópia no aparelho ANTES de enviar
    savedClientId.current = body.client_id;
    const res = await post(body);
    setBusy(false);
    const ok = res.status === 200 || res.status === 201;
    const refused = res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429;
    if (refused) {
      // o servidor recusou (ex.: início fora do prazo): sai da fila e o atleta é avisado
      removeFromOutbox(ls, userId, body.client_id, Date.now());
      setSendError(true);
      return;
    }
    if (ok) removeFromOutbox(ls, userId, body.client_id, Date.now());
    setActivityId(ok ? res.activityId ?? null : null);
    update(null);
    setSavedOpen(true);
  };
  const closeSaved = () => { setSavedOpen(false); savedClientId.current = null; setActivityId(null); };

  const act = tm ? timedActiveMs(tm, now) : 0;

  return (
    <>
      {!tm && <p className="muted run-timed-note">{sport === "treadmill" ? t("Esteira: o EAPace marca o tempo do treino.") : t("Fortalecimento: o EAPace marca o tempo do treino.")}</p>}
      {tm && tm.status !== "finalizing" && (
        <dl className="run-metrics" role="group" aria-label={t("Métricas da gravação")}>
          <div className="run-metric main"><dt>{t("Tempo ativo")}</dt><dd role="timer" aria-live="off">{formatDuration(act / 1000)}</dd></div>
          <div className="run-metric"><dt>{t("Tempo decorrido")}</dt><dd style={{ fontSize: "1.25rem" }}>{formatDuration(timedElapsedMs(tm, now) / 1000)}</dd></div>
          <div className="run-metric"><dt>{t("Situação")}</dt><dd style={{ fontSize: "1.25rem" }}>{tm.status === "paused" ? t("Pausado") : t("Gravando")}</dd></div>
        </dl>
      )}
      {tm?.status === "finalizing" && (
        <section aria-labelledby="timed-sum">
          <h2 id="timed-sum" style={{ fontSize: "1.2rem" }}>{tm.sport === "treadmill" ? t("Resumo da esteira") : t("Resumo do fortalecimento")}</h2>
          {timedLongEnough(tm) ? (
            <dl className="run-metrics">
              <div className="run-metric main"><dt>{t("Tempo ativo")}</dt><dd>{formatDuration(tm.activeMs / 1000)}</dd></div>
              <div className="run-metric"><dt>{t("Tempo decorrido")}</dt><dd style={{ fontSize: "1.25rem" }}>{formatDuration(timedElapsedMs(tm, now) / 1000)}</dd></div>
            </dl>
          ) : (
            <div className="run-note warn" role="alert"><p>{t("Gravação curta demais para virar atividade (mínimo 10 segundos).")}</p><p>{t("Você pode descartar e gravar de novo.")}</p></div>
          )}
          {sendError && <div className="run-note danger" role="alert"><p>{t("O servidor recusou esta atividade. Descarte e grave de novo.")}</p></div>}
        </section>
      )}
      {pendingCount > 0 && !tm && <p className="muted" role="status">{t("Há atividade aguardando envio: ela vai sozinha quando houver internet.")}</p>}

      {askDiscard && tm && (
        <div className="run-note danger" role="alertdialog" aria-label={t("Confirmar descarte")}>
          <p>{t("Descartar apaga esta gravação do aparelho. Não há outra cópia. Continuar?")}</p>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="danger" onClick={() => { setAskDiscard(false); setSendError(false); update(null); }}>{t("Sim, descartar")}</Button>
            <Button onClick={() => setAskDiscard(false)}>{t("Voltar")}</Button>
          </div>
        </div>
      )}
      {tm && !askDiscard && <div><Button variant="ghost" onClick={() => setAskDiscard(true)}>{t("Descartar gravação")}</Button></div>}

      <div className="run-controls">
        {!tm && <Button variant="primary" onClick={() => update(startTimed(sport, newId(), Date.now()))}>{sport === "treadmill" ? t("Iniciar esteira") : t("Iniciar fortalecimento")}</Button>}
        {tm?.status === "running" && <><Button onClick={() => update(pauseTimed(tm, Date.now()))}>{t("Pausar")}</Button><Button variant="primary" onClick={() => update(finishTimed(tm, Date.now()))}>{t("Finalizar")}</Button></>}
        {tm?.status === "paused" && <><Button variant="primary" onClick={() => update(resumeTimed(tm, Date.now()))}>{t("Retomar")}</Button><Button onClick={() => update(finishTimed(tm, Date.now()))}>{t("Finalizar")}</Button></>}
        {tm?.status === "finalizing" && <Button variant="primary" busy={busy} disabled={!timedLongEnough(tm)} onClick={() => void save()}>{t("Salvar atividade")}</Button>}
      </div>

      <Modal
        open={savedOpen}
        title={t("Atividade salva")}
        onClose={closeSaved}
        footer={
          <>
            <Button onClick={closeSaved}>{t("Fechar")}</Button>
            <Button variant="primary" disabled={!activityId} onClick={() => { const id = activityId; closeSaved(); if (id) navigate(`/org/${orgId}/atleta/atividades/${id}`); }}>{t("Ver atividade")}</Button>
          </>
        }
      >
        <p role="status">
          {activityId
            ? t("A atividade foi enviada e já está em Atividades.")
            : t("A atividade ficou guardada no aparelho e será enviada assim que houver internet. Depois ela aparece em Atividades.")}
        </p>
      </Modal>
    </>
  );
}
